import React, { useState, useEffect, useCallback } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { ATTESTATION_CLAUSES } from "./SEC_AttestationClauses";
import {
  getRepresentativeAttestations,
  signRepresentativeAttestation,
} from "./representativeInteractionService";

/**
 * 🎯 Stepwise attestation signer — one clause active at a time, prior clauses
 * locked/checked, mirrors the oped_wmos AttestationSigner UX. Unlike that
 * reference, signing here is real: each click persists a timestamped row via
 * Corp.Z_04_RepresentativeAttestation. S3 push (ESG_AFFIRMATIONS/<COMPANY>/...)
 * is NOT yet implemented — nrgCore has no S3 client today; S3Key stays null
 * until that's built as a follow-up.
 */
export default function SEC_AttestationSigner({ corporateEntityId, onComplete }) {
  const [signedKeys, setSignedKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");

  const siteKey = process.env.REACT_APP_SITE_KEY;

  const loadSigned = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getRepresentativeAttestations(corporateEntityId, { siteKey });
      setSignedKeys((rows || []).map((r) => GoldenSourceHelper.get(r, "ClauseKey")));
    } finally {
      setLoading(false);
    }
  }, [corporateEntityId, siteKey]);

  useEffect(() => {
    loadSigned();
  }, [loadSigned]);

  const currentIndex = ATTESTATION_CLAUSES.findIndex((c) => !signedKeys.includes(c.key));
  const isComplete = currentIndex === -1;

  useEffect(() => {
    if (isComplete && !loading && onComplete) onComplete();
  }, [isComplete, loading, onComplete]);

  const handleSign = async (clauseKey) => {
    setSigning(true);
    setError("");
    try {
      await signRepresentativeAttestation(corporateEntityId, clauseKey, null, { siteKey });
      setSignedKeys((prev) => [...prev, clauseKey]);
    } catch (err) {
      setError(err.message || "Signing failed.");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border spinner-border-sm text-primary" />
      </div>
    );
  }

  return (
    <div className="card shadow-sm border-0 p-4">
      <h6 className="fw-bold text-uppercase extra-small text-muted mb-3 text-primary">
        Attestation
      </h6>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {isComplete ? (
        <div className="text-center py-3">
          <i className="bi bi-check-circle-fill text-success h2 d-block mb-2"></i>
          <p className="fw-bold mb-0">Attestation Complete</p>
        </div>
      ) : (
        <ul className="list-group list-group-flush">
          {ATTESTATION_CLAUSES.map((clause, i) => {
            const isSigned = signedKeys.includes(clause.key);
            const isCurrent = i === currentIndex;
            return (
              <li key={clause.key} className="list-group-item px-0 py-3">
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <p className={`mb-0 small ${!isSigned && !isCurrent ? "text-muted" : ""}`}>
                    {clause.text}
                  </p>
                  {isSigned && <span className="badge bg-success flex-shrink-0">Signed</span>}
                  {!isSigned && !isCurrent && (
                    <span className="badge bg-light text-muted border flex-shrink-0">Pending</span>
                  )}
                  {isCurrent && (
                    <button
                      className="btn btn-sm btn-dark fw-bold extra-small px-3 flex-shrink-0"
                      disabled={signing}
                      onClick={() => handleSign(clause.key)}
                    >
                      {signing ? "SIGNING..." : `SIGN TERM ${i + 1}`}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
